/**
 * CORE-BL — Guided Onboarding & Product Education · the one step authority
 *
 * > **Never present an empty experience.**
 *
 * and, just as importantly: **never present a different one to the runtime
 * than to the menu.**
 *
 * A previous revision filtered correctly here and then let `TutorialContext`
 * walk the RAW track — `getStepsForFlow(state.flow)` — for the current step,
 * next, previous, jump, gating and completion. So a dispatcher could be
 * offered a front-of-house walkthrough with intake filtered out and then be
 * walked straight through intake anyway. Two step authorities is worse than
 * one wrong one, because each looks correct in its own tests.
 *
 * `resolveExperienceSteps` is now the only place a sequence is produced, and
 * every consumer — menu, HUD, coachmark, route watcher, gate check, progress
 * count — reads what it returns.
 */

import {
  MANAGER_STEPS,
  USER_STEPS,
  type OnboardingStep,
} from '@/contexts/OnboardingSteps';
import {
  DETAIL_EXPERIENCE_STEPS,
  FOH_EXPERIENCE_STEPS,
  MANAGER_OPS_EXPERIENCE_STEPS,
  ORG_OVERVIEW_EXPERIENCE_STEPS,
  PARTS_EXPERIENCE_STEPS,
  READONLY_EXPERIENCE_STEPS,
  TECHNICIAN_EXPERIENCE_STEPS,
  applicableSteps,
  type CapabilitySnapshot,
  type RoleAwareStep,
} from './roleExperienceSteps';
import {
  TRACK_FOR_EXPERIENCE,
  UNRESOLVED_MESSAGE,
  type KnownAppRole,
  type ExperienceResolution,
  type OnboardingExperience,
  type OnboardingTrack,
  type UnresolvedReason,
} from './onboardingExperience';

const LIBRARY: Readonly<Record<OnboardingTrack, ReadonlyArray<RoleAwareStep>>> =
  Object.freeze({
    manager: MANAGER_STEPS,
    user: USER_STEPS,
    technician: TECHNICIAN_EXPERIENCE_STEPS,
    foh: FOH_EXPERIENCE_STEPS,
    parts: PARTS_EXPERIENCE_STEPS,
    readonly: READONLY_EXPERIENCE_STEPS,
    org_overview: ORG_OVERVIEW_EXPERIENCE_STEPS,
    // Core-100 R4 (OD-14): the Detail Specialist's own track.
    detail: DETAIL_EXPERIENCE_STEPS,
    // Core-100 R4 W5 (CORE-BL-GAP-011): running the day, for owners and managers.
    manager_ops: MANAGER_OPS_EXPERIENCE_STEPS,
    // The LEGACY 'technician' key and 'setup_wizard' resolve here. The legacy
    // key genuinely had no content, and that is precisely what its historical
    // rows record — so it must keep resolving to nothing.
    none: [],
  });

export function baseStepsForExperience(
  experience: OnboardingExperience,
): ReadonlyArray<RoleAwareStep> {
  return LIBRARY[TRACK_FOR_EXPERIENCE[experience]] ?? [];
}

export type ExperienceStepsResult =
  | {
      status: 'steps';
      experience: OnboardingExperience;
      role: KnownAppRole;
      steps: OnboardingStep[];
    }
  | {
      status: 'unavailable';
      /**
       * Narrowed from `string`. Every value this can hold is an
       * `UnresolvedReason`, and typing it loosely meant a consumer could not
       * tell `route_blocked` from `no_content` without a cast — which is
       * exactly the distinction that was being collapsed at the boundary.
       */
      reason: UnresolvedReason;
      message: string;
    };

/**
 * The sequence this person actually receives.
 *
 * Applicability runs BEFORE the emptiness check, because a track can be
 * populated in the library and still filter to nothing for a particular role.
 * Checking the library would miss exactly the case this capability exists to
 * prevent.
 */
export function resolveExperienceSteps(
  resolution: ExperienceResolution,
  capabilities: CapabilitySnapshot,
): ExperienceStepsResult {
  if (resolution.status === 'unresolved') {
    return {
      status: 'unavailable',
      reason: resolution.reason,
      message: UNRESOLVED_MESSAGE[resolution.reason],
    };
  }

  const { experience, role, workRoles } = resolution;
  const steps = applicableSteps(
    baseStepsForExperience(experience),
    role,
    capabilities,
    workRoles,
  );

  if (steps.length === 0) {
    // Distinguish "nothing written" from "written, but the router will not
    // let this role open the pages it lives on". Both end in no tour; only
    // one is CORE-BL's own gap, and calling an access problem a content
    // problem would misdescribe whose it is.
    const authored = baseStepsForExperience(experience).length > 0;
    const reason = authored ? 'route_blocked' : 'no_content';
    return {
      status: 'unavailable',
      reason,
      message: UNRESOLVED_MESSAGE[reason],
    };
  }

  return { status: 'steps', experience, role, steps };
}

/**
 * The sequence for an experience the runtime is ALREADY in.
 *
 * `TutorialContext` holds a persisted flow and a live capability snapshot but
 * may not have a resolution object to hand. This gives it the same filtered
 * sequence the resolver would, so the runtime and the entry point cannot
 * disagree about what somebody is being taught.
 *
 * `role` is optional because a step's role gate is a narrowing device; when
 * the role is unknown only capability-gated steps are filtered, which is the
 * conservative direction.
 */
export function stepsForActiveExperience(
  experience: OnboardingExperience,
  role: KnownAppRole | null,
  capabilities: CapabilitySnapshot,
  workRoles?: ReadonlyArray<string> | null,
): OnboardingStep[] {
  return applicableSteps(baseStepsForExperience(experience), role, capabilities, workRoles);
}

export function experienceHasContent(
  experience: OnboardingExperience,
  role: KnownAppRole,
  capabilities: CapabilitySnapshot,
): boolean {
  return stepsForActiveExperience(experience, role, capabilities).length > 0;
}
